import {ShapeItem} from '@PosterWhiteboard/items/shape-item/shape-item.class';
import type {RGBA} from '@Utils/color.util';
import {rgbaToHexString, rgbaToRGBAString} from '@Utils/color.util';
import type {CopyableItemStyles} from '@PosterWhiteboard/items/item/item.types';
import {type InitItemOpts, ITEM_TYPE, type ItemObject} from '@PosterWhiteboard/items/item/item.types';
import type {FillObject} from '@PosterWhiteboard/classes/fill.types';
import {FillTypes, getFillColors} from '@PosterWhiteboard/classes/fill.types';
import {FillSurface, withStockLayoutForSurface} from '@PosterWhiteboard/classes/fill-surface';
import {assertNever} from '@Utils/assert-never.util';
import {cloneAsImageWithAngleIgnored, getPaths} from '@Utils/fabric.util';
import type {VectorItemObject} from '@PosterWhiteboard/items/vector-item/vector-item.types';
import type {VectorItemSource} from '@PosterWhiteboard/items/vector-item/vector-item.library';
import {arePathsCustomizable, getLinearGradientForVectorItem, getRadialGradientForVectorItem, loadVectorSVG} from '@PosterWhiteboard/items/vector-item/vector-item.library';
import type {FabricObject, Path} from '@postermywall/fabricjs-2';
import {util, Group} from '@postermywall/fabricjs-2';
import type {DeepPartial} from '@/global';

const MAX_VECTOR_PATHS = 200;

export class VectorItem extends ShapeItem {
  declare _fabricObject: Group | Path;

  public gitype = ITEM_TYPE.VECTOR;
  public source!: VectorItemSource;
  public fileName = '';

  public isCustomisable = true;

  /**
   * Treat the vector as single-fill even when its SVG paths have differing colors: every path is painted with
   * the item's fill. Persisted so a flattened shape stays flattened (and recolorable) when the design reloads.
   */
  public forceUniformFill = false;

  override get fabricObject(): Group | Path {
    return this._fabricObject;
  }

  public override getFillSurface(): FillSurface {
    return FillSurface.VECTOR;
  }

  /** A vector draws every gradient its own way, so a gradient moved onto it keeps only its colors and takes the vector's plain layout. */
  public override getMovedFill(fill: FillObject): FillObject {
    return withStockLayoutForSurface(fill, FillSurface.VECTOR);
  }

  public override toObject(): VectorItemObject {
    return {
      ...super.toObject(),
      fileName: this.fileName,
      source: this.source,
      isCustomisable: this.isCustomisable,
      forceUniformFill: this.forceUniformFill,
    };
  }

  protected override async onItemInitialized(): Promise<void> {
    if (this.hasTooManyPaths(this.fabricObject)) {
      await this.convertVectorToImage();
    }
    await this.updateFabricObject();
  }

  protected override isVisible(): boolean {
    return (this.fabricObject.fill && this.fabricObject.opacity > 0) || this.border.solidBorderThickness > 0;
  }

  protected override async getFabricObjectForItem(opts?: InitItemOpts): Promise<FabricObject> {
    const {paths} = await loadVectorSVG(this.fileName, this.source);
    const fabricOpts = {
      ...this.getInitOptionsForView(),
    };
    this.isCustomisable = this.forceUniformFill || arePathsCustomizable(paths);
    this.preparePaths(paths);

    const svg = util.groupSVGElements(paths, fabricOpts);
    svg.set({
      uniformScaling: !(this.fileName === '803' || this.fileName === '804'),
      ...this.getScalesAndShadowToMaintainScaledDimensions(svg.width, svg.height, opts),
    });
    return svg;
  }

  protected hasSinglePath(): boolean {
    return !(this.fabricObject instanceof Group);
  }

  protected override async updateFabricObjectFromObject(obj: DeepPartial<VectorItemObject>): Promise<void> {
    await super.updateFabricObjectFromObject(obj);

    if (obj.fill !== undefined && this.isCustomisable) {
      const fill = this.#getFabricFill(this.getFillObject());
      this.fabricObject.set('fill', fill);
      this.applyFillToPaths(fill);
    }
  }

  /**
   * Vectors keep the Fabric fills they have always drawn with: a linear gradient in Fabric's percentage units, so every path of a multi-path
   * SVG ramps over its own height, and a radial one laid over the whole vector with its stops pulled in, since most SVGs leave space between
   * their control box and their shape.
   */
  #getFabricFill(fill: FillObject): FabricObject['fill'] {
    const colors = getFillColors(fill);
    switch (fill.fillType) {
      case FillTypes.NONE:
        return null;
      case FillTypes.SOLID:
        return rgbaToRGBAString(colors[0]);
      case FillTypes.LINEAR_GRADIENT:
        return getLinearGradientForVectorItem(colors);
      case FillTypes.RADIAL_GRADIENT:
        return getRadialGradientForVectorItem(colors, this.fabricObject.width, this.fabricObject.height);
      default:
        return assertNever(fill);
    }
  }

  protected override async reInitFabricObject(): Promise<void> {
    await super.reInitFabricObject();
    this.applyFillToPaths(this.fabricObject.fill);
  }

  private applyFillToPaths(fill: FabricObject['fill']): void {
    if (this.fabricObject instanceof Group) {
      const paths = getPaths(this.fabricObject);
      for (const item of paths) {
        item.set('fill', fill);
      }
    }
  }

  public override applyBorder(): void {
    if (this.isCustomisable) {
      const paths = getPaths(this.fabricObject);
      const strokeHex = rgbaToHexString(this.border.solidBorderColor);

      for (const item of paths) {
        item.set({
          stroke: strokeHex,
        });
      }

      this.fabricObject.set({
        ...this.border.getBorder(),
      });
    }
  }

  public override getColors(): RGBA[] {
    const colors: RGBA[] = super.getColors();

    colors.push(...getFillColors(this.getFillObject()));

    colors.push(this.border.solidBorderColor);

    return colors;
  }

  public override getCopyableStyles(): CopyableItemStyles {
    return {
      ...super.getCopyableStyles(),
      border: this.border.toObject(),
      frontFill: this.getFillObject(),
      textBorder: this.border.toObject(),
    };
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<VectorItemObject> = {};
    if (copiedStyles.textBorder !== undefined) {
      stylesObj.border = this.getBorderFromCopiedStyles(copiedStyles.textBorder);
    }

    if (copiedStyles.frontFill !== undefined) {
      stylesObj.fill = this.getMovedFill(copiedStyles.frontFill);
    }

    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['scaleX', 'scaleY']);
    await this.applyPastedStyles({...commonStylesObj, ...stylesObj});
  }

  protected override isRoundedBorderSupported(): boolean {
    return false;
  }

  protected override itemObjectHasDestructiveChanges(oldItemObject: VectorItemObject): boolean {
    if (!this.fabricObject) {
      return false;
    }

    if (this.isCustomisable && !this.hasSinglePath()) {
      if (oldItemObject.border.solidBorderThickness !== this.border.solidBorderThickness) {
        return true;
      }
    }
    return false;
  }

  protected override getInitOptionsForView(): Record<string, unknown> {
    return {
      perPixelTargetFind: true,
      selectable: true,
      lockUniScaling: false,
    };
  }

  private preparePaths(paths: FabricObject[]): void {
    if (this.isCustomisable) {
      for (const path of paths) {
        path.set({
          strokeWidth: this.border.solidBorderThickness,
          strokeUniform: true,
        });
      }
    }
  }

  private hasTooManyPaths(svg: FabricObject): boolean {
    return getPaths(svg).length > MAX_VECTOR_PATHS;
  }

  public hasMultiplePaths(): boolean {
    return getPaths(this.fabricObject).length > 1;
  }

  private async convertVectorToImage(): Promise<void> {
    this.applyBorder();
    const img = await cloneAsImageWithAngleIgnored(this.fabricObject, {
      multiplier: this.page.poster.scaling.scale,
    });
    if (!img.width || !img.height) {
      throw new Error('Failed to get image for vector');
    }

    img.set({
      scaleX: this.fabricObject.scaleX * (this.fabricObject.get('width') / img.width),
      scaleY: this.fabricObject.scaleY * (this.fabricObject.get('height') / img.height),
    });
    this.setFabricObject(img);
    this.isCustomisable = false;
  }
}
